import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateOnlyStringToDate } from "@insite/client-framework/Common/Utilities/DateUtilities";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getVmiLocationState } from "@insite/client-framework/Store/Data/VmiLocations/VmiLocationsSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import loadDiscrepancyCollection from "@tKgpco/Store/Data/DiscrepancyHistory/Handlers/loadDiscrepancyCollection";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Tag, { horizontalStyles, TagPresentationProps } from "@insite/mobius/Tag";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import loadRMACollection from "@tKgpco/Store/Data/RMA/Handlers/loadRMACollection";

const mapStateToProps = (state: CustomApplicationState) => {
    const vmiLocationId = state.pages.orderHistory.getOrdersParameter.vmiLocationId;

    return {
        filtersOpen: state.KGPState.RMAStore.isFilterOpen,
        getOrdersParameter: state.KGPState.RMAStore.RMACollectionParams,
        vmiLocationState: vmiLocationId ? getVmiLocationState(state, vmiLocationId) : null,
        language: getSession(state).language,
    };
};

const mapDispatchToProps = {
    updateSearchFields,
    loadRMACollection,
};

export interface RMAHistorySearchTagsStyles {
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
}

export const RMAHistorySearchTagsStyles: RMAHistorySearchTagsStyles = {
    appliedFiltersContainer: { css: horizontalStyles },
};

const styles = RMAHistorySearchTagsStyles;

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const RMAHistorySearchTags = ({ getOrdersParameter, language, loadRMACollection }: Props) => {
    const fieldRemoveHandler = (fieldName: string, value = "") => {
        loadRMACollection({ ...getOrdersParameter, [fieldName]: value });
    };

    return (
        <StyledWrapper {...styles.appliedFiltersContainer}>
            {getOrdersParameter?.rmaNumber && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("rmaNumber");
                    }}
                >
                    {`${translate("RMA #")}: ${getOrdersParameter?.rmaNumber}`}
                </Tag>
            )}
            {getOrdersParameter?.orderNumber && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("orderNumber");
                    }}
                >
                    {`${translate("Order #")}: ${getOrdersParameter?.orderNumber}`}
                </Tag>
            )}
            {getOrdersParameter?.poNumber && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("poNumber");
                    }}
                >
                    {`${translate("PO #")}: ${getOrdersParameter.poNumber}`}
                </Tag>
            )}
            {getOrdersParameter?.status && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("status");
                    }}
                >
                    {`${translate("Status")}: ${getOrdersParameter.status}`}
                </Tag>
            )}
            {getOrdersParameter?.fromDate && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("fromDate");
                    }}
                    data-test-selector="orderHistoryTags_FromDate"
                >
                    {`${translate("From")}: ${getLocalizedDateTime({
                        dateTime: convertDateOnlyStringToDate(getOrdersParameter.fromDate)!,
                        language,
                    })}`}
                </Tag>
            )}
            {getOrdersParameter?.toDate && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("toDate");
                    }}
                >
                    {`${translate("To")}: ${getLocalizedDateTime({
                        dateTime: convertDateOnlyStringToDate(getOrdersParameter.toDate)!,
                        language,
                    })}`}
                </Tag>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RMAHistorySearchTags),
    definition: {
        group: "Common",
        displayName: "RMA Tags",
    },
};

export default widgetModule;
